import {
  SelectContent,
  SelectItem,
  Select as SelectPrimitive,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { cn } from '@workspace/ui/lib';
import type { LucideIcon } from 'lucide-react';
import Image from 'next/image';
import React from 'react';

// import { useTranslations } from 'next-intl';

interface SelectProps {
  size?: 'base' | 'sm';
  options: SelectOption[];
  value: string;
  onChange: (value: string, option?: SelectOption) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  onBlur?: () => void;
  name?: string;
  readOnly?: boolean;
}

export interface SelectOption {
  name: string;
  value: string;
  icon?: LucideIcon;
  image?: {
    path: string;
    alt: string;
  };
  disabled?: boolean;
}

export const Select = ({
  size = 'base',
  options,
  value,
  onChange,
  placeholder,
  className,
  triggerClassName,
  disabled,
  onBlur,
  name,
  readOnly,
  ...props
}: SelectProps) => {
  // const t = useTranslations('form');
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedOption = React.useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const isOpenRef = React.useRef(false);

  const resolvedPlaceholder = readOnly ? 'Not provided' : (placeholder ?? 'Select');

  return (
    <div className={cn('group flex flex-col gap-2', className)} aria-disabled={disabled}>
      <SelectPrimitive
        value={value}
        onValueChange={(newValue: string) => {
          const option = options.find((opt) => opt.value === newValue);
          onChange(newValue, option);
          isOpenRef.current = false;
        }}
        disabled={disabled}
        name={name}
        open={readOnly ? false : isOpen}
        onOpenChange={(open: boolean) => {
          setIsOpen(open);
          isOpenRef.current = open;
          if (!open && !value) {
            onBlur?.();
          }
        }}
        {...props}
      >
        <SelectTrigger
          disabled={disabled}
          hasSelected={!!value}
          readOnly={readOnly}
          className={cn(
            triggerClassName,
            'cursor-pointer',
            readOnly && 'text-grey-600 dark:text-grey-600',
            size === 'sm' ? 'h-9 rounded-xl px-3 py-2.5 text-sm' : 'cursor-pointer'
          )}
        >
          {value ? (
            <div className="flex items-center gap-3">
              {selectedOption?.image && (
                <Image
                  src={selectedOption.image.path}
                  alt={selectedOption.image.alt}
                  width={24}
                  height={24}
                  priority
                />
              )}
              {selectedOption?.name}
            </div>
          ) : (
            <SelectValue placeholder={resolvedPlaceholder} />
          )}
        </SelectTrigger>
        <SelectContent className="z-[1000]">
          {options.map((option) => {
            const visualElement = option.icon ? (
              <div className="-ml-2 flex size-12 items-center justify-center rounded-2xl bg-pink-400 dark:bg-pink-400">
                <option.icon className="size-6 text-neutral-100 dark:text-neutral-100" />
              </div>
            ) : option.image ? (
              <Image
                src={option.image.path}
                alt={option.image.alt || option.name}
                width={24}
                height={24}
              />
            ) : null;

            return (
              <SelectItem
                key={option.value}
                value={option.value}
                visualElement={visualElement}
                disabled={option.disabled}
                className="cursor-pointer"
              >
                {option.name}
              </SelectItem>
            );
          })}
        </SelectContent>
      </SelectPrimitive>
    </div>
  );
};
